import { ProgressSpinner } from "primereact/progressspinner"
import styles from "../../styles/Loader.module.css"
import { gql } from "@apollo/client"
import { DateTime } from "luxon"
import ImageWithLoader from "./ImageWithLoader"

export const staffNameInitials = (name) => {
    return name.first.charAt(0).toUpperCase() + name.last.charAt(0).toUpperCase()
}

export const getActualUTCTime = () => {
    return DateTime.utc().toISO()
}

export const formatDate = (date, format) => {
    return DateTime.fromISO(date).toFormat(format)
}

export const sortListByDate = (list, dateField) => {
    list.sort((a, b) => {
        let dateA = DateTime.fromISO(a[dateField])
        let dateB = DateTime.fromISO(b[dateField])
        return dateB - dateA
    })
}

export function LoaderMedium() {
    return (
        <div className={styles.wrapperMed}>
            <ProgressSpinner
                style={{ width: "50px", height: "50px" }}
                strokeWidth="4"
                animationDuration=".4s"
            />
        </div>
    )
}

export const findName = (id, list) => {
    const staff = list.find((staff) => staff.staffId === id);
    return staff && staff.staffName
        ? {
            first: staff.staffName.staff_name_first || "",
            last: staff.staffName.staff_name_last || "",
            full_name: staff.staffName.staff_full_name || ""
        }
        : { first: "", last: "", full_name: "" };
};

export const normalizeIso = (s) => {
    if (!s) return s;
    // Truncate fractional seconds to max 3 digits, preserving timezone (Z or ±HH:MM)
    return s.replace(/(\.\d{3})\d*(Z|[+\-]\d{2}:?\d{2})?$/, "$1$2");
};

export const getTimeAgo = (date) => {
    if (!date || date === "0001-01-01T00:00:00") {
        return "";
    }

    const normalized = normalizeIso(date);
    const dateTime = DateTime.fromISO(normalized, { zone: "utc" }).setZone("local");
    const now = DateTime.local();
    const diffYears = now.diff(dateTime, "years").years;
    const diffDays = now.diff(dateTime, "days").days;
    const diffHours = now.diff(dateTime, "hours").hours;
    const diffMins = now.diff(dateTime, "minutes").minutes;

    if (diffMins < 1) {
        return "Less than a minute ago";
    }

    if (diffHours < 12) {
        return dateTime.toRelative({ unit: ["hours", "minutes"] }); // Less than 12 hours ago
    }

    if (dateTime.hasSame(now, "day")) {
        return "Today"; // Same day
    }

    if (dateTime.hasSame(now.minus({ days: 1 }), "day")) {
        return "Yesterday"; // One day ago
    }

    if (diffDays < 7) {
        return dateTime.toFormat("EEEE"); // Day of the week (e.g., Sunday)
    }

    if (diffYears > 1) {
        return dateTime.toRelative({ unit: ["years"] }); // More than 1 year old, return empty string
    }

    return dateTime.toFormat("MM/dd/yyyy"); // U.S. date format (e.g., 01/25/2025)
};


export const groupMessagesByDate = (messages) => {
    let groupedMessages = []
    let lastFromId = null
    let lastKey = null
    let lastGroupIndex = -1

    messages.forEach(message => {
        const relativeTime = getTimeAgo(message.dateUpdated) //DateTime.fromISO(message.dateUpdated).toRelative();
        const key = `${relativeTime}_${message.fromId}`

        if (key === lastKey && lastFromId === message.fromId) {
            groupedMessages[lastGroupIndex].messages.push(message)
        }
        else {
            groupedMessages.push({
                key: key,
                messages: [message]
            })
            lastGroupIndex++
        }
        lastFromId = message.fromId
        lastKey = key
    })

    return groupedMessages
}

export const GET_STAFF_NAMES = gql`
  query getStaffNamesByIds($staff_id: ID!, $organization_id: ID!, $staff_ids: [ID!]!) {
    getStaffNamesByIds(staff_id:$staff_id, organization_id:$organization_id, staff_ids: $staff_ids) {
        data
    }
  }
`

export const GET_ORGANIZATION = gql`
    query organization($staff_id: ID!, $organization_id: ID!) {
      organization: organization(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        organization_name
      }
    }
  `

const displayBody = (body) => {
    //if body contains \r\n means it's a multiline message and should show it with line breaks
    if (body.includes("\r\n")) {
        return body.split("\r\n").map((line, i) => (
            <p key={i}>{line}</p>
        ))
    } else {
        return <p>{body}</p>
    }
}

export const transformMessage = (message) => {
    //if mge contains a twilio or gcp bucket link means it's an image and should show it as image
    let regex = ""
    if (message.body.includes("https://p.twil.io")) {
        regex = /https:\/\/p\.twil\.io\/\S+/g
    } else if (message.body.includes("https://storage.googleapis.com")) {
        regex = /https:\/\/storage\.googleapis\.com\/\S+/g
    }
    if (regex !== "") {
        let links = message.body.match(regex)
        let textWithoutLinks = message.body.replace(regex, '').trim()
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {displayBody(textWithoutLinks)}
                {links.map((link, i) => (
                    <ImageWithLoader aKey={i} src={link} width="150" />
                ))}
            </div>
        )
    } else {
        if (message.mediaUrl) {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {displayBody(message.body)}
                    <ImageWithLoader src={message.mediaUrl} width="150" />
                </div>
            )
        }
        return displayBody(message.body)
    }
}
